import React from 'react'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import './InputSelect.scss'

interface InputSelectProps {
  value?: any
  onChange?: (value: any) => void
  disabled?: boolean
  title?: string
  hint?: string
  allowNull?: boolean
  multiple?: boolean
  items?: any[]
  bindLabel?: string
  placeholder?: string
  error?: string
  menuPortalTarget?: HTMLElement | null
}

export const InputSelect: React.FC<InputSelectProps> = ({
  value,
  onChange,
  disabled = false,
  title,
  hint,
  allowNull = true,
  multiple = false,
  items = [],
  bindLabel = 'name',
  placeholder,
  error,
  menuPortalTarget,
}) => {
  const { t } = useTranslation()

  const handleChange = (selectedOption: any) => {
    if (multiple) {
      const selectedValues = selectedOption ? selectedOption.map((option: any) => option.value) : []
      onChange?.(selectedValues)
    } else {
      const selectedValue = selectedOption ? selectedOption.value : null
      onChange?.(selectedValue)
    }
  }

  const getValue = () => {
    if (multiple) {
      return items
        .filter((item) => value && value.includes(item.id))
        .map((item) => ({
          value: item.id,
          label: item[bindLabel],
        }))
    } else {
      if (value === null || value === undefined || value === 0) {
        return null
      }
      const selectedItem = items.find((item) => item.id === value)
      return selectedItem ? { value: selectedItem.id, label: selectedItem[bindLabel] } : null
    }
  }

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderColor: error ? 'var(--bs-danger)' : state.isFocused ? 'var(--bs-primary)' : 'var(--bs-border-color)',
      boxShadow: error
        ? '0 0 0 0.25rem rgba(220, 53, 69, 0.25)'
        : state.isFocused
          ? `0 0 0 0.25rem hsla(var(--pngx-primary), var(--pngx-primary-lightness), var(--pngx-focus-alpha))`
          : 'none',
      '&:hover': {
        borderColor: error ? 'var(--bs-danger)' : 'var(--bs-primary)',
      },
    }),
    multiValue: (provided: any) => ({
      ...provided,
      backgroundColor: '#6c757d',
      color: '#ffffff',
      borderRadius: '0.375rem',
      padding: '0.25rem 0.5rem',
      margin: '0.125rem',
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: '#ffffff',
      fontSize: '0.875rem',
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: '#ffffff',
      ':hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        color: '#ffffff',
      },
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: '#212529',
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 1055,
    }),
  }

  return (
    <div className={`docdot-input-select ${disabled ? 'disabled' : ''} ${error ? 'is-invalid' : ''}`}>
      {title && (
        <label className="form-label mb-2" htmlFor="inputId">
          {title}
        </label>
      )}
      <Select
        isMulti={multiple}
        options={items.map((item) => ({
          value: item.id,
          label: item[bindLabel],
        }))}
        value={getValue()}
        onChange={handleChange}
        placeholder={placeholder || t('select_option')}
        className="react-select-container"
        classNamePrefix="react-select"
        isClearable={allowNull}
        isDisabled={disabled}
        isSearchable={true}
        styles={customStyles}
        menuPortalTarget={menuPortalTarget || undefined}
        menuPosition={menuPortalTarget ? 'fixed' : 'absolute'}
        filterOption={(option, inputValue) => {
          return option.label.toLowerCase().includes(inputValue.toLowerCase())
        }}
        components={{
          ClearIndicator: ({ innerProps }) => (
            <div {...innerProps} className="react-select__clear-indicator" title={t('clear_selection')}>
              <i className="bi bi-x" style={{ fontSize: '1rem' }}></i>
            </div>
          ),
        }}
      />
      {error && <div className="invalid-feedback">{error}</div>}
      {hint && <small className="form-text text-muted">{hint}</small>}
    </div>
  )
}
